import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Crown, Gift, Star, HandCoins, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, PageHeader } from "./shared";

const todayStr = () => new Date().toISOString().split("T")[0];
const emptyPlan = { name: "", price: "", discount_percent: "", validity_days: "365", description: "" };
const emptyPkg = { name: "", price: "", total_sessions: "", amount_paid: "", payment_method: "Cash", notes: "" };

const CustomerPicker = ({ customers, picked, onPick, testId }) => {
  const [q, setQ] = useState("");
  const matches = q.trim() ? customers.filter((c) => (c.name + " " + c.phone).toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6) : [];
  return (
    <div className="relative">
      {picked ? (
        <div className="flex items-center justify-between bg-[#0A3F2F]/5 border border-[#0A3F2F]/20 rounded-md px-3 py-2.5 text-sm" data-testid={`${testId}-picked`}>
          <span><span className="font-semibold">{picked.name}</span> <span className="text-[#5C6A63]">· {picked.phone}</span></span>
          <button type="button" onClick={() => onPick(null)} className="text-gray-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <input data-testid={`${testId}-input`} className={inputCls} placeholder="🔍 Find client by name or phone…" value={q} onChange={(e) => setQ(e.target.value)} />
      )}
      {!picked && matches.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7E5] rounded-md shadow-lg max-h-48 overflow-y-auto">
          {matches.map((c) => (
            <button key={c.id} type="button" onClick={() => { onPick(c); setQ(""); }} data-testid={`${testId}-option-${c.id}`}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAFAF8] border-b border-[#E5E7E5] last:border-0">
              <span className="font-semibold">{c.name}</span> <span className="text-[#5C6A63]">· {c.phone}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Loyalty() {
  const [tab, setTab] = useState("memberships");
  const [plans, setPlans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState({ enabled: true, earn_rate: 5, point_value: 1 });
  const [planOpen, setPlanOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [assignCustomer, setAssignCustomer] = useState(null);
  const [assignPlanId, setAssignPlanId] = useState("");
  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgCustomer, setPkgCustomer] = useState(null);
  const [pkgForm, setPkgForm] = useState(emptyPkg);
  const [collecting, setCollecting] = useState(null);
  const [collectAmt, setCollectAmt] = useState("");
  const [adjusting, setAdjusting] = useState(null);
  const [adjust, setAdjust] = useState({ delta: "", reason: "" });

  const load = useCallback(() => {
    api.get("/membership-plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    api.get("/customer-packages").then(({ data }) => setPackages(data)).catch(() => {});
    api.get("/loyalty/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePlan = async (e) => {
    e.preventDefault();
    const payload = { name: planForm.name, price: Number(planForm.price) || 0, discount_percent: Number(planForm.discount_percent) || 0, validity_days: Number(planForm.validity_days) || 365, description: planForm.description };
    if (!payload.name || !payload.discount_percent) { toast.error("Plan name and discount % are required"); return; }
    try {
      if (editingPlan) { await api.put(`/membership-plans/${editingPlan.id}`, payload); toast.success("Plan updated"); }
      else { await api.post("/membership-plans", payload); toast.success("Plan created"); }
      setPlanOpen(false); setEditingPlan(null); setPlanForm(emptyPlan); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deletePlan = async (id) => {
    try { await api.delete(`/membership-plans/${id}`); toast.success("Plan deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const assignMembership = async () => {
    if (!assignCustomer || !assignPlanId) { toast.error("Pick a client and a plan"); return; }
    try {
      await api.post(`/customers/${assignCustomer.id}/membership`, { plan_id: assignPlanId });
      toast.success(`Membership assigned to ${assignCustomer.name}`);
      setAssignCustomer(null); setAssignPlanId(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeMembership = async (c) => {
    try { await api.delete(`/customers/${c.id}/membership`); toast.success(`Membership removed for ${c.name}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sellPackage = async (e) => {
    e.preventDefault();
    if (!pkgCustomer) { toast.error("Pick a client"); return; }
    if (!pkgForm.name || !pkgForm.price || !pkgForm.total_sessions) { toast.error("Package name, price and sessions are required"); return; }
    try {
      await api.post("/customer-packages", {
        customer_id: pkgCustomer.id, name: pkgForm.name, price: Number(pkgForm.price),
        total_sessions: Number(pkgForm.total_sessions),
        amount_paid: pkgForm.amount_paid === "" ? null : Number(pkgForm.amount_paid),
        payment_method: pkgForm.payment_method, notes: pkgForm.notes,
      });
      toast.success(`Package sold to ${pkgCustomer.name}`);
      setPkgOpen(false); setPkgCustomer(null); setPkgForm(emptyPkg); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const redeemSession = async (p) => {
    try {
      const { data } = await api.post(`/customer-packages/${p.id}/use`, { note: "Redeemed from Loyalty page" });
      toast.success(`Session used — ${data.total_sessions - data.sessions_used} left in ${data.name}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const collectPkg = async (e) => {
    e.preventDefault();
    const amt = Number(collectAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const { data } = await api.post(`/customer-packages/${collecting.id}/payment`, { amount: amt });
      toast.success(data.balance_due <= 0 ? "Package fully paid!" : `₹${amt.toLocaleString("en-IN")} collected — ₹${data.balance_due.toLocaleString("en-IN")} still due`);
      setCollecting(null); setCollectAmt(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deletePkg = async (id) => {
    try { await api.delete(`/customer-packages/${id}`); toast.success("Package deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.post("/loyalty/settings", { enabled: settings.enabled, earn_rate: Number(settings.earn_rate) || 0, point_value: Number(settings.point_value) || 0 });
      toast.success("Reward points settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveAdjust = async (e) => {
    e.preventDefault();
    const delta = Math.trunc(Number(adjust.delta));
    if (!delta) { toast.error("Enter points to add (or negative to remove)"); return; }
    try {
      const { data } = await api.post(`/customers/${adjusting.id}/points`, { delta, reason: adjust.reason });
      toast.success(`${adjusting.name} now has ${data.points} points`);
      setAdjusting(null); setAdjust({ delta: "", reason: "" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const members = customers.filter((c) => c.membership);
  const pointCustomers = customers.filter((c) => (c.points || 0) !== 0).sort((a, b) => (b.points || 0) - (a.points || 0));

  return (
    <div className="space-y-6" data-testid="loyalty-page">
      <PageHeader title="Loyalty" subtitle="Memberships, prepaid packages and reward points." />
      <div className="flex gap-2 flex-wrap">
        {[["memberships", "Memberships", Crown], ["packages", "Prepaid Packages", Gift], ["points", "Reward Points", Star]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`loyalty-tab-${k}`}
            className={`flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full transition-colors ${tab === k ? "bg-[#0A3F2F] text-white" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {tab === "memberships" && (
        <div className="space-y-6" data-testid="memberships-tab">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-[#1C1C1C]">Membership Plans</h2>
            <button onClick={() => { setEditingPlan(null); setPlanForm(emptyPlan); setPlanOpen(true); }} data-testid="add-plan-btn"
              className="bg-[#0A3F2F] text-white text-xs font-semibold px-4 py-2 rounded-full flex items-center gap-1.5 hover:bg-[#0d5540]">
              <Plus className="w-3.5 h-3.5" /> Add Plan
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p.id} className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm" data-testid={`plan-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold text-[#0A3F2F]">{p.name}</p>
                    <p className="text-2xl font-bold text-[#1C1C1C] mt-1">₹{p.price.toLocaleString("en-IN")}</p>
                  </div>
                  <span className="bg-[#C5A059]/20 text-[#8a6d33] text-xs font-bold px-2.5 py-1 rounded-full">{p.discount_percent}% off</span>
                </div>
                <p className="text-xs text-[#5C6A63] mt-2">{p.description || `Valid ${p.validity_days} days`} · {p.validity_days} days</p>
                <div className="flex gap-3 mt-3">
                  <button onClick={() => { setEditingPlan(p); setPlanForm({ name: p.name, price: String(p.price), discount_percent: String(p.discount_percent), validity_days: String(p.validity_days), description: p.description || "" }); setPlanOpen(true); }}
                    data-testid={`edit-plan-${p.id}`} className="text-gray-400 hover:text-[#0A3F2F]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deletePlan(p.id)} data-testid={`delete-plan-${p.id}`} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm space-y-3 max-w-xl" data-testid="assign-membership-card">
            <h2 className="text-sm font-bold text-[#1C1C1C]">Assign Membership</h2>
            <CustomerPicker customers={customers} picked={assignCustomer} onPick={setAssignCustomer} testId="assign-customer" />
            <div className="flex gap-2">
              <select data-testid="assign-plan-select" className={inputCls} value={assignPlanId} onChange={(e) => setAssignPlanId(e.target.value)}>
                <option value="">Select plan…</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{`${p.name} — ₹${p.price} (${p.discount_percent}% off)`}</option>)}
              </select>
              <button onClick={assignMembership} data-testid="assign-membership-btn"
                className="whitespace-nowrap bg-[#C5A059] text-[#1C1C1C] text-sm font-bold px-5 rounded-md hover:bg-[#d4b06c]">Assign</button>
            </div>
            <p className="text-[11px] text-[#5C6A63]">Tip: bill the membership fee from the Billing desk as a custom item.</p>
          </div>

          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Member</th>
                  <th className="px-4 py-3 font-semibold">Plan</th>
                  <th className="px-4 py-3 font-semibold">Discount</th>
                  <th className="px-4 py-3 font-semibold">Valid Till</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="members-table-body">
                {members.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5C6A63]" data-testid="no-members">No members yet — assign a plan above.</td></tr>}
                {members.map((c) => {
                  const active = c.membership.expiry_date >= todayStr();
                  return (
                    <tr key={c.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`member-row-${c.id}`}>
                      <td className="px-4 py-3"><p className="font-semibold">{c.name}</p><p className="text-xs text-[#5C6A63]">{c.phone}</p></td>
                      <td className="px-4 py-3">{c.membership.plan_name}</td>
                      <td className="px-4 py-3 font-semibold text-[#8a6d33]">{c.membership.discount_percent}%</td>
                      <td className="px-4 py-3">{c.membership.expiry_date}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${active ? "bg-emerald-600/10 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                          {active ? "Active" : "Expired"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => removeMembership(c)} data-testid={`remove-membership-${c.id}`} title="Remove membership"
                          className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "packages" && (
        <div className="space-y-4" data-testid="packages-tab">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-[#1C1C1C]">Prepaid Packages (session tracking)</h2>
            <button onClick={() => { setPkgCustomer(null); setPkgForm(emptyPkg); setPkgOpen(true); }} data-testid="sell-package-btn"
              className="bg-[#0A3F2F] text-white text-xs font-semibold px-4 py-2 rounded-full flex items-center gap-1.5 hover:bg-[#0d5540]">
              <Plus className="w-3.5 h-3.5" /> Sell Package
            </button>
          </div>
          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Client</th>
                  <th className="px-4 py-3 font-semibold">Package</th>
                  <th className="px-4 py-3 font-semibold">Sessions</th>
                  <th className="px-4 py-3 font-semibold">Price</th>
                  <th className="px-4 py-3 font-semibold">Payment</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="packages-table-body">
                {packages.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5C6A63]" data-testid="no-packages">No packages sold yet.</td></tr>}
                {packages.map((p) => {
                  const left = p.total_sessions - (p.sessions_used || 0);
                  return (
                    <tr key={p.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`package-row-${p.id}`}>
                      <td className="px-4 py-3"><p className="font-semibold">{p.customer_name}</p><p className="text-xs text-[#5C6A63]">{p.phone}</p></td>
                      <td className="px-4 py-3">{p.name}<p className="text-[10px] text-[#5C6A63]">sold {p.date}</p></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#0A3F2F]" style={{ width: `${(p.sessions_used / p.total_sessions) * 100}%` }} />
                          </div>
                          <span className="text-xs font-semibold" data-testid={`sessions-left-${p.id}`}>{left}/{p.total_sessions} left</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-bold text-[#0A3F2F]">₹{p.price.toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3">
                        {p.balance_due > 0 ? (
                          <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#C5A059]/20 text-[#8a6d33]">Due ₹{p.balance_due.toLocaleString("en-IN")}</span>
                        ) : (
                          <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-600/10 text-emerald-700">Paid</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-3 items-center">
                          {left > 0 && (
                            <button onClick={() => redeemSession(p)} data-testid={`use-session-${p.id}`}
                              className="text-xs font-bold text-[#0A3F2F] underline underline-offset-4 whitespace-nowrap">Use session</button>
                          )}
                          {p.balance_due > 0 && (
                            <button onClick={() => { setCollecting(p); setCollectAmt(String(p.balance_due)); }} data-testid={`collect-package-${p.id}`} title="Collect payment"
                              className="text-[#C5A059] hover:text-[#0A3F2F]"><HandCoins className="w-4 h-4" /></button>
                          )}
                          <button onClick={() => deletePkg(p.id)} data-testid={`delete-package-${p.id}`} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#5C6A63]">Sessions can also be redeemed directly on a bill — open Billing, pick the client and tap their package.</p>
        </div>
      )}

      {tab === "points" && (
        <div className="space-y-6" data-testid="points-tab">
          <form onSubmit={saveSettings} className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm space-y-4 max-w-xl" data-testid="points-settings-card">
            <h2 className="text-sm font-bold text-[#1C1C1C]">Reward Points Settings</h2>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" data-testid="points-enabled-checkbox" checked={settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} />
              Enable reward points on bills
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Points earned per ₹100 billed</label>
                <input data-testid="points-earn-rate-input" type="number" min="0" step="any" className={inputCls} value={settings.earn_rate} onChange={(e) => setSettings({ ...settings, earn_rate: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Value of 1 point (₹)</label>
                <input data-testid="points-value-input" type="number" min="0" step="any" className={inputCls} value={settings.point_value} onChange={(e) => setSettings({ ...settings, point_value: e.target.value })} />
              </div>
            </div>
            <button type="submit" data-testid="save-points-settings-btn" className="bg-[#0A3F2F] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#0d5540]">
              Save Settings
            </button>
          </form>

          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Client</th>
                  <th className="px-4 py-3 font-semibold">Points</th>
                  <th className="px-4 py-3 font-semibold">Worth</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="points-table-body">
                {pointCustomers.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#5C6A63]" data-testid="no-points">No clients with points yet — points are earned automatically on bills.</td></tr>}
                {pointCustomers.map((c) => (
                  <tr key={c.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`points-row-${c.id}`}>
                    <td className="px-4 py-3"><p className="font-semibold">{c.name}</p><p className="text-xs text-[#5C6A63]">{c.phone}</p></td>
                    <td className="px-4 py-3 font-bold text-[#0A3F2F]" data-testid={`points-balance-${c.id}`}>★ {c.points}</td>
                    <td className="px-4 py-3 text-[#5C6A63]">₹{((c.points || 0) * (Number(settings.point_value) || 0)).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => { setAdjusting(c); setAdjust({ delta: "", reason: "" }); }} data-testid={`adjust-points-${c.id}`}
                        className="text-xs font-bold text-[#0A3F2F] underline underline-offset-4">Adjust</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingPlan ? `Edit Plan — ${editingPlan.name}` : "New Membership Plan"}</DialogTitle></DialogHeader>
          <form onSubmit={savePlan} className="space-y-3" data-testid="plan-form">
            <input data-testid="plan-name-input" className={inputCls} placeholder="Plan name (e.g. Gold Membership) *" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} />
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="plan-price-input" type="number" min="0" className={inputCls} placeholder="Price ₹" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} />
              <input data-testid="plan-discount-input" type="number" min="0" max="100" className={inputCls} placeholder="Discount % *" value={planForm.discount_percent} onChange={(e) => setPlanForm({ ...planForm, discount_percent: e.target.value })} />
              <input data-testid="plan-validity-input" type="number" min="1" className={inputCls} placeholder="Days" value={planForm.validity_days} onChange={(e) => setPlanForm({ ...planForm, validity_days: e.target.value })} />
            </div>
            <textarea data-testid="plan-description-input" className={inputCls} rows={2} placeholder="Description (optional)" value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} />
            <button type="submit" data-testid="plan-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              {editingPlan ? "Save Changes" : "Create Plan"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Sell Prepaid Package</DialogTitle></DialogHeader>
          <form onSubmit={sellPackage} className="space-y-3" data-testid="package-form">
            <CustomerPicker customers={customers} picked={pkgCustomer} onPick={setPkgCustomer} testId="pkg-customer" />
            <input data-testid="pkg-name-input" className={inputCls} placeholder="Package name (e.g. 6 Facial Sessions) *" value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="pkg-price-input" type="number" min="0" className={inputCls} placeholder="Package price ₹ *" value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} />
              <input data-testid="pkg-sessions-input" type="number" min="1" className={inputCls} placeholder="Total sessions *" value={pkgForm.total_sessions} onChange={(e) => setPkgForm({ ...pkgForm, total_sessions: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="pkg-paid-input" type="number" min="0" className={inputCls} placeholder="Amount paid now (blank = full)" value={pkgForm.amount_paid} onChange={(e) => setPkgForm({ ...pkgForm, amount_paid: e.target.value })} />
              <select data-testid="pkg-method-select" className={inputCls} value={pkgForm.payment_method} onChange={(e) => setPkgForm({ ...pkgForm, payment_method: e.target.value })}>
                <option>Cash</option><option>UPI</option><option>Card</option>
              </select>
            </div>
            <input data-testid="pkg-notes-input" className={inputCls} placeholder="Notes (optional)" value={pkgForm.notes} onChange={(e) => setPkgForm({ ...pkgForm, notes: e.target.value })} />
            <button type="submit" data-testid="pkg-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Sell Package
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!collecting} onOpenChange={(v) => !v && setCollecting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Collect Payment — {collecting?.name}</DialogTitle></DialogHeader>
          <form onSubmit={collectPkg} className="space-y-4" data-testid="pkg-collect-form">
            <div className="bg-[#FAFAF8] rounded-md p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#5C6A63]">Client</span><span className="font-semibold">{collecting?.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6A63]">Package price</span><span>₹{collecting?.price?.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between font-bold text-red-600"><span>Balance due</span><span>₹{(collecting?.balance_due ?? 0).toLocaleString("en-IN")}</span></div>
            </div>
            <input data-testid="pkg-collect-amount-input" type="number" min="1" className={inputCls} value={collectAmt} onChange={(e) => setCollectAmt(e.target.value)} />
            <button type="submit" data-testid="pkg-collect-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Record Payment
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjusting} onOpenChange={(v) => !v && setAdjusting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Adjust Points — {adjusting?.name}</DialogTitle></DialogHeader>
          <form onSubmit={saveAdjust} className="space-y-4" data-testid="adjust-points-form">
            <p className="text-sm text-[#5C6A63]">Current balance: <span className="font-bold text-[#0A3F2F]">★ {adjusting?.points || 0} points</span></p>
            <input data-testid="adjust-delta-input" type="number" className={inputCls} placeholder="e.g. 50 to add, -20 to remove" value={adjust.delta} onChange={(e) => setAdjust({ ...adjust, delta: e.target.value })} />
            <input data-testid="adjust-reason-input" className={inputCls} placeholder="Reason (optional)" value={adjust.reason} onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} />
            <button type="submit" data-testid="adjust-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Update Points
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
